import React from 'react';
import { useAuth } from './AuthContext';
import { useService } from './ServiceContext';
import { usePayment } from './PaymentContext';

// 平台預設佣金設定（可被 Admin 透過本機存儲覆蓋，用於前端模擬/調整）
const defaultCommissionConfig = {
  baseCommissionRate: 0.15,
  premiumCommissionRate: 0.1,
  matchingFee: 5,
  emergencyFeeMultiplier: 1.5,
  videoConsultationFee: 25,
  warrantyFee: 15,
  volumeDiscountRates: [
    { threshold: 1000, discountRate: 0.02 },
    { threshold: 5000, discountRate: 0.05 },
    { threshold: 10000, discountRate: 0.08 },
  ],
  serviceCategoryRates: {
    1: 0.12, 2: 0.14, 3: 0.13, 4: 0.16, 5: 0.11,
    6: 0.18, 7: 0.10, 8: 0.09, 9: 0.12, 10: 0.08,
  },
  timeBasedRates: {
    peakHours: { start: 18, end: 22, multiplier: 1.2 },
    weekendMultiplier: 1.15,
    holidayMultiplier: 1.25,
  },
  qualityBonusRates: { 5: -0.02, 4: -0.01, 3: 0, 2: 0.01, 1: 0.02 },
  loyaltyDiscountRates: { 10: 0.01, 50: 0.02, 100: 0.03, 200: 0.05 },
} as const;

// 嘗試從本機存儲載入自訂佣金設定（僅前端有效，不影響後端計算）
function getMergedCommissionConfig() {
  try {
    if (typeof globalThis !== 'undefined' && globalThis.localStorage) {
      const raw = globalThis.localStorage.getItem('commissionConfig');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          return { ...defaultCommissionConfig, ...parsed };
        }
      }
    }
  } catch (error) {
    console.warn('Failed to load commissionConfig from localStorage, using defaults', error);
  }
  return defaultCommissionConfig;
}

// 创建兼容性 hook，只能在 Provider 内部使用
export function useAppContext() {
  const auth = useAuth();
  const service = useService();
  const payment = usePayment();

  return {
    // Auth 相关
    currentUser: auth.currentUser,
    setCurrentUser: auth.setCurrentUser,
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.isLoading,
    login: auth.login,
    register: auth.register,
    verifyEmail: auth.verifyEmail,
    resendVerificationCode: auth.resendVerificationCode,
    logout: auth.logout,
    updateProfile: auth.updateProfile,
    forgotPassword: auth.forgotPassword,
    resetPassword: auth.resetPassword,

    // Service 相关
    serviceCategories: service?.serviceCategories || [],
    activeRequest: service?.activeRequest || null,
    setActiveRequest: service?.setActiveRequest || (() => {}),
    nearbyCraftsmen: service?.nearbyCraftsmen || [],
    setNearbyCraftsmen: service?.setNearbyCraftsmen || (() => {}),
    selectedCraftsman: service?.selectedCraftsman || null,
    setSelectedCraftsman: service?.setSelectedCraftsman || (() => {}),
    fetchNearbyCraftsmen: service?.fetchNearbyCraftsmen || (async () => {}),
    createServiceRequest: service?.createServiceRequest || (async () => ({} as any)),

    // Payment 相关
    paymentMethods: payment?.paymentMethods || [],
    selectedPaymentMethod: payment?.selectedPaymentMethod || null,
    setSelectedPaymentMethod: payment?.setSelectedPaymentMethod || (() => {}),
    paymentStatus: payment?.paymentStatus || 'idle',
    setPaymentStatus: payment?.setPaymentStatus || (() => {}),
    processPayment: payment?.processPayment || (async () => ({} as any)),

    // 其他功能（从原来的 AppContext 中保留）
    currentLocation: null,
    setCurrentLocation: () => {},
    commissionConfig: getMergedCommissionConfig(),
    platformMetrics: {
      totalUsers: 0,
      totalCraftsmen: 0,
      totalRequests: 0,
      totalRevenue: 0,
      averageRating: 0,
      responseTime: '5 min',
    },
    calculateServiceFees: () => ({}),
    calculateBulkServiceFees: () => [],
    getCommissionStatistics: () => ({}),
    predictCommission: () => ({}),
    registerCraftsman: async () => '',
    registerCompany: async () => '',
  };
}
